import { useEffect, useMemo, useState } from 'react';
import { Template, TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import {
  ListPersonsLegacyRequest,
  PersonsResponse,
} from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons_service.pb';
import { InvitationRecipient } from '@weave/schema-gen-ts/dist/schemas/reviews/invitation/v1/service.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQueries, UseQueryOptions } from 'react-query';
import { ReviewInvitationMutations } from '@frontend/api-reviews-invitation';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { useTranslation } from '@frontend/i18n';
import {
  ContextPreview,
  getStaticTemplateId,
  ManualTemplateTypes,
  MultiSendViewModalContents,
  SendInThreadModal,
  TemplateSelector,
  useGetDefaultTemplate,
} from '@frontend/integrated-messaging';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { PersonSelectorComponents, PersonSelectorHooks } from '@frontend/person-selector';
import { SchemaPersonV3Service } from '@frontend/schema';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, Modal, ModalControlModalProps, useAlert } from '@frontend/design-system';

type BulkReviewInvitationsModalProps = ModalControlModalProps & {
  maxContactSelection?: number;
};
type BulkReviewInvitationStep = 'recipients' | 'send' | 'template-selection';

export const BulkReviewInvitationsModal = ({ maxContactSelection = 10, ...rest }: BulkReviewInvitationsModalProps) => {
  const alert = useAlert();
  const { t } = useTranslation('bulk-review-invitations');
  const { selectedLocationIds, accessibleLocationData, selectedOrgId } = useAppScopeStore();
  const [currentStep, setCurrentStep] = useState<BulkReviewInvitationStep>('recipients');
  const [templateString, setTemplateString] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');

  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const templateTypes = useMemo<ManualTemplateTypes[]>(() => {
    const resolvedVertical =
      lastUsedVertical !== null && lastUsedVertical !== Vertical.INVALID_VERTICAL
        ? lastUsedVertical
        : accessibleLocationData[selectedLocationIds[0]]?.vertical;
    const result: ManualTemplateTypes[] = [TemplateType_Slug.MANUAL_REVIEW_REQUEST];
    if (resolvedVertical === Vertical.VET) result.push(TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET);
    return result;
  }, [lastUsedVertical, JSON.stringify(accessibleLocationData), selectedLocationIds[0]]);
  const singleTemplateType =
    lastUsedVertical === Vertical.VET
      ? TemplateType_Slug.MANUAL_REVIEW_REQUEST_PET
      : TemplateType_Slug.MANUAL_REVIEW_REQUEST;

  const templateListQuery = TemplatorV2Queries.useTemplatesList({
    businessGroupIds: selectedLocationIds,
    templateTypeSlugs: templateTypes,
  });
  const { mutateAsync: sendReviewInvitation } = ReviewInvitationMutations.useBatchCreateReviewInvitationMutation({
    onSuccess: (res, req) => {
      alert.success(
        !!req.sendAt
          ? t('{{count}} Messages Scheduled', { count: res.invitations.length })
          : t('{{count}} Messages Sent', { count: res.invitations.length })
      );
      rest.onClose();
    },
    onError: (_, req) => {
      alert.error(
        !!req.sendAt
          ? t('Error scheduling messages. Please try again.')
          : t('Error sending messages. Please try again.')
      );
    },
  });
  const getLastUsedTemplate = useGetDefaultTemplate({ groupIds: selectedLocationIds });
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();

  const personSelectorState = PersonSelectorHooks.useMultiSelectRecipientList({
    groupIds: selectedLocationIds,
    maxSelection: maxContactSelection,
    searchValue: recipientSearch,
    setSearchValue: setRecipientSearch,
  });

  // TODO: switch to looking up location access on persons when new endpoint is made available
  const personLocationQueries = useQueries(
    selectedLocationIds.map((locationId) => {
      const request: ListPersonsLegacyRequest = {
        locationIds: [locationId],
        personIds: personSelectorState.selectedPersons.map(({ personId }) => personId),
      };
      const query: UseQueryOptions<PersonsResponse, unknown, PersonsResponse & { locationId: string }> = {
        queryKey: ['persons-v3', 'list-persons-legacy', request],
        queryFn: () => SchemaPersonV3Service.ListPersonsLegacy(request),
        enabled: currentStep !== 'recipients' && selectedLocationIds.length > 1,
        select: (data) => ({
          ...data,
          locationId,
        }),
      };

      return query;
    })
  );
  const locationIdsWithAssociatedPersonIds =
    selectedLocationIds.length > 1
      ? selectedLocationIds
          .map((locationId, index) => ({
            locationId,
            personIds: personLocationQueries[index]?.data?.persons.map(({ personId }) => personId) ?? [],
          }))
          .sort((a, b) =>
            a.personIds.length > b.personIds.length ? -1 : a.personIds.length === b.personIds.length ? 0 : 1
          )
      : [
          {
            locationId: selectedLocationIds[0],
            personIds: personSelectorState.selectedPersons.map(({ personId }) => personId),
          },
        ];
  const { recipients, recipientsWithoutLocation } = personSelectorState.selectedPersons.reduce<{
    recipients: InvitationRecipient[];
    recipientsWithoutLocation: Person[];
  }>(
    (acc, curr) => {
      const firstLocation = locationIdsWithAssociatedPersonIds.find(({ personIds }) =>
        personIds.includes(curr.personId)
      );
      if (firstLocation) {
        acc.recipients.push({ person: curr, locationId: firstLocation.locationId });
      } else {
        acc.recipientsWithoutLocation.push(curr);
      }
      return acc;
    },
    { recipients: [], recipientsWithoutLocation: [] }
  );

  const reset = () => {
    personSelectorState.reset();
    setRecipientSearch('');
    setCurrentStep('recipients');
    setTemplateSelectionCount(0);
    const lastUsedTemplate = getLastUsedTemplate({
      templates: templateListQuery.data?.templates ?? [],
      type: singleTemplateType,
    });
    setSelectedTemplate(lastUsedTemplate);
    setTemplateString(lastUsedTemplate?.templateString ?? '');
  };

  useEffect(() => {
    if (templateListQuery.data && !selectedTemplate) {
      const lastUsedTemplate = getLastUsedTemplate({
        templates: templateListQuery.data.templates,
        type: singleTemplateType,
      });
      setSelectedTemplate(lastUsedTemplate);
      setTemplateString(lastUsedTemplate?.templateString ?? '');
    }
  }, [!!templateListQuery.data]);

  const [templateSelectionCount, setTemplateSelectionCount] = useState(0);

  const handleSendOrSchedule = ({ sendAt, pausable }: { sendAt?: string; pausable?: boolean } = {}) => {
    if (recipients.length) {
      sendReviewInvitation({
        orgId: selectedOrgId,
        recipients,
        templateId:
          !selectedTemplate || selectedTemplate?.templateId === getStaticTemplateId(singleTemplateType)
            ? '' // Don't pass template id if using default static template, use template string instead
            : selectedTemplate?.templateId ?? '',
        timezone:
          accessibleLocationData[selectedLocationIds[0]].timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        // TODO: get the locale dynamically once we have a way to do so
        locale: 'en_US',
        templateString,
        sendAt,
        pausable,
      });
    }
    if (recipientsWithoutLocation.length) {
      alert.error(
        t('Error finding location associated with {{count}} contacts', { count: recipientsWithoutLocation.length })
      );
    }
  };

  useEffect(() => {
    // Wait for modal close animation before resetting state
    setTimeout(() => {
      if (!rest.show) {
        reset();
      }
    }, 300);
  }, [rest.show]);

  return (
    <Modal css={{ minWidth: 'min(700px, 90vw)', maxHeight: 'min(800px, 90vh)' }} {...rest}>
      {currentStep === 'recipients' ? (
        <PersonSelectorComponents.MultiSelectRecipientModalContents
          {...personSelectorState}
          maxSelection={maxContactSelection}
          onSelect={() => setCurrentStep('send')}
          onClose={rest.onClose}
        />
      ) : currentStep === 'send' ? (
        personSelectorState.selectedPersons.length === 1 ? (
          <SendInThreadModal
            onBack={() => {
              setCurrentStep('recipients');
            }}
            groupId={recipients?.[0]?.locationId ?? selectedLocationIds[0]}
            personId={personSelectorState.selectedPersons[0].personId}
            templateType={singleTemplateType}
            selectedTemplate={selectedTemplate}
            openTemplateSelector={() => setCurrentStep('template-selection')}
            contextPreviewProps={{
              iconName: 'star',
              title: selectedTemplate?.name || t('Review Request'),
              subtitle: t('Review Request'),
            }}
            {...rest}
          />
        ) : (
          <MultiSendViewModalContents
            templateTypes={templateTypes}
            groupIds={selectedLocationIds}
            contactCount={personSelectorState.selectedPersons.length}
            initialTemplateString={selectedTemplate?.templateString ?? ''}
            templateString={templateString}
            setTemplateString={setTemplateString}
            onClose={rest.onClose}
            onSend={handleSendOrSchedule}
            onSchedule={handleSendOrSchedule}
            onSubmit={handleSendOrSchedule}
            onBack={() => {
              setCurrentStep('recipients');
            }}
            openTemplateSelector={() => setCurrentStep('template-selection')}
            templateEditorKey={`${selectedTemplate?.templateId ?? 'none'}-${templateSelectionCount}`}
            disableSend={
              selectedLocationIds.length > 1
                ? !!recipientsWithoutLocation.length && personLocationQueries.some((query) => query.isLoading)
                : !personSelectorState.selectedPersons.length
            }
          >
            <ContextPreview
              iconName='star'
              title={selectedTemplate?.name || t('Review Request')}
              subtitle={t('Review Request')}
            />
          </MultiSendViewModalContents>
        )
      ) : currentStep === 'template-selection' ? (
        <>
          <Modal.Header onClose={rest.onClose}>
            <Button
              variant='secondary'
              onClick={() => setCurrentStep('send')}
              iconName='back'
              css={{ marginRight: theme.spacing(1) }}
            />
            {t('Select a Review Template')}
          </Modal.Header>
          <Modal.Body
            css={{
              paddingTop: theme.spacing(1),
            }}
          >
            <TemplateSelector
              groupIds={selectedLocationIds}
              templateTypes={templateTypes}
              staticDefaultTemplateTypes={[singleTemplateType]}
              onSelectTemplate={(template) => {
                setSelectedTemplate(template);
                setTemplateSelectionCount(0);
                setTemplateString(template.templateString);
                setCurrentStep('send');
              }}
            />
          </Modal.Body>
        </>
      ) : null}
    </Modal>
  );
};
